import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Typography,
  Tabs,
  Tab,
  Card,
  CardContent,
  Stack,
  Avatar,
  Button,
  CircularProgress,
  Alert
} from '@mui/material';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import TimeOffTab from './TimeOffTab';
import { useBusiness } from '../../context/BusinessContext';
import StaffShiftCalendar from './StaffShiftCalendar';
import useThemeStyles from '../../theme/useThemeStyles';

const DAY_KEYS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];

const createDefaultWorkingHours = () => {
  const base = {};
  DAY_KEYS.forEach((day, idx) => {
    base[day] = {
      available: idx < 5,
      start: '09:00',
      end: '17:00',
      breaks: []
    };
  });
  return base;
};

function StaffMemberDetail() {
  const styles = useThemeStyles();
  const { memberId } = useParams();
  const navigate = useNavigate();
  const { selectedBusiness, businessId } = useBusiness();
  const [activeTab, setActiveTab] = useState('calendar');
  const [member, setMember] = useState(null);
  const [schedule, setSchedule] = useState(null);
  const [workingHours, setWorkingHours] = useState(createDefaultWorkingHours());
  const [timeOff, setTimeOff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingSchedule, setSavingSchedule] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setError('');
      try {
        const [memberRes, scheduleRes] = await Promise.allSettled([
          api.get(`/api/business-members/${memberId}`),
          api.get(`/api/team-member-schedules/team-member/${memberId}`)
        ]);

        if (memberRes.status === 'fulfilled') {
          setMember(memberRes.value.data);
        } else {
          setError('Could not load staff member');
        }

        if (scheduleRes.status === 'fulfilled') {
          const sched = scheduleRes.value.data;
          setSchedule(sched);
          setWorkingHours(sched.workingHours || createDefaultWorkingHours());
          setTimeOff(sched.timeOff || []);
        } else {
          setSchedule(null);
          setWorkingHours(createDefaultWorkingHours());
          setTimeOff([]);
        }
      } catch (err) {
        setError('Unable to load staff details');
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [memberId]);

  const memberName = useMemo(() => {
    if (!member?.user) return 'Staff member';
    return `${member.user.firstName || ''} ${member.user.lastName || ''}`.trim() || 'Staff member';
  }, [member]);

  const ensureScheduleExists = async () => {
    if (schedule?._id) return schedule;
    // create new schedule before adding time off
    const payload = {
      teamMemberId: memberId,
      workingHours,
      businessId: selectedBusiness?.business?._id
    };
    const res = await api.post('/api/team-member-schedules', payload);
    setSchedule(res.data);
    return res.data;
  };

  const _handleSaveSchedule = async () => {
    setSavingSchedule(true);
    setMessage('');
    setError('');
    try {
      if (schedule?._id) {
        const res = await api.put(`/api/team-member-schedules/${schedule._id}` , { workingHours });
        setSchedule(res.data);
        setTimeOff(res.data.timeOff || []);
      } else {
        const res = await api.post('/api/team-member-schedules', {
          teamMemberId: memberId,
          workingHours,
          businessId: selectedBusiness?.business?._id
        });
        setSchedule(res.data);
        setTimeOff(res.data.timeOff || []);
      }
      setMessage('Schedule updated');
    } catch (err) {
      setError(err?.response?.data?.message || 'Unable to save schedule');
    } finally {
      setSavingSchedule(false);
    }
  };

  const handleAddTimeOff = async (entry, onSuccess) => {
    try {
      const sched = await ensureScheduleExists();
      const res = await api.post(`/api/team-member-schedules/${sched._id}/time-off`, {
        startDate: entry.startDate,
        endDate: entry.endDate,
        reason: entry.reason,
        description: entry.description
      });
      setTimeOff([...timeOff, { ...res.data.timeOff }]);
      onSuccess?.();
    } catch (err) {
      setError(err?.response?.data?.message || 'Unable to add time off');
    }
  };

  const handleUpdateTimeOff = async (timeOffId, entry, onSuccess) => {
    try {
      if (!schedule?._id) {
        setError('Save a schedule before editing time off');
        return;
      }
      const res = await api.put(`/api/team-member-schedules/${schedule._id}/time-off/${timeOffId}`, {
        startDate: entry.startDate,
        endDate: entry.endDate,
        reason: entry.reason,
        description: entry.description
      });
      setTimeOff(timeOff.map((t) => (t._id === timeOffId ? { ...t, ...res.data.timeOff } : t)));
      onSuccess?.();
    } catch (err) {
      setError(err?.response?.data?.message || 'Unable to update time off');
    }
  };

  const handleDeleteTimeOff = async (entry) => {
    try {
      if (!schedule?._id || !entry._id) return;
      await api.delete(`/api/team-member-schedules/${schedule._id}/time-off/${entry._id}`);
      setTimeOff(timeOff.filter((t) => t._id !== entry._id));
    } catch (err) {
      setError(err?.response?.data?.message || 'Unable to delete time off');
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Button variant="text" onClick={() => navigate('/supplier/staff')} sx={{ mb: 2 }}>
        ← Back to Staff
      </Button>

      <Stack spacing={2} sx={{ mb: 2 }}>
        <Typography component="h2" className="pagename" sx={styles.text.pagename}>
          {memberName}
        </Typography>
        <Typography variant="body2" color="text.secondary">Manage profile, weekly schedule, and time off.</Typography>
        {message && <Alert severity="success" onClose={() => setMessage('')}>{message}</Alert>}
        {error && <Alert severity="error" onClose={() => setError('')}>{error}</Alert>}
      </Stack>

      <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)} sx={{ mb: 3 }}>
        <Tab label="Profile" value="profile" />
        <Tab label="Calendar" value="calendar" />
        <Tab label="Time Off" value="timeOff" />
      </Tabs>

      {activeTab === 'profile' && (
        <Card>
          <CardContent>
            <Stack direction="row" spacing={2} alignItems="center">
              <Avatar src={member?.user?.profilePicture}>{memberName.charAt(0)}</Avatar>
              <Box>
                <Typography variant="h6">{memberName}</Typography>
                <Typography variant="body2" color="text.secondary">{member?.user?.email}</Typography>
                <Typography variant="body2" color="text.secondary">Role: {member?.role || '—'}</Typography>
                <Typography variant="body2" color="text.secondary">Status: {member?.status || '—'}</Typography>
              </Box>
            </Stack>
          </CardContent>
        </Card>
      )}

      {activeTab === 'calendar' && (
        <StaffShiftCalendar
          teamMemberId={memberId}
          businessId={businessId || selectedBusiness?.business?._id}
        />
      )}

      {activeTab === 'timeOff' && (
        <TimeOffTab
          timeOff={timeOff}
          onAdd={handleAddTimeOff}
          onUpdate={handleUpdateTimeOff}
          onDelete={handleDeleteTimeOff}
          busy={savingSchedule}
        />
      )}
    </Box>
  );
}

export default StaffMemberDetail;
